import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  apiClient,
  restaurantOrderService,
  ORDER_STATUSES,
  type RestaurantOrder,
  type RestaurantOrderStatus,
} from '@kss/api';
import type { DineRootState } from './dineSlice';

export interface DineOrderTicket {
  id: string;
  code: string;
  status: RestaurantOrderStatus;
  placedAt?: string;
  items?: Array<{ name: string; quantity: number; note?: string }>;
}

/**
 * Polls the live status of every order this dine-in session has
 * placed. Powers the Orders tab's status timeline + "your food is
 * ready" callout.
 *
 * Primary path: `GET /api/restaurant/dine/{qrToken}/orders` (§41),
 * returns every order for this table. The endpoint exists in
 * swagger but currently requires auth (G54) — once backend drops
 * [Authorize] the customer's tab gets live order status with no FE
 * change.
 *
 * Fallback: fan out `restaurantOrderService.get(id)` per placed-order
 * id. Requires auth too, so only works if a guest user is
 * provisioned per §32.
 */
export function useDineOrders(): {
  tickets: DineOrderTicket[];
  loading: boolean;
  refresh: () => void;
} {
  const { qrToken = '' } = useParams<{ qrToken: string }>();
  const placedIds = useSelector((s: DineRootState) => s.dine.placedOrderIds);
  const [tickets, setTickets] = useState<DineOrderTicket[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Try the dine-anonymous endpoint — one call, every order.
      if (qrToken) {
        try {
          const r = await apiClient.get<{ orders?: RestaurantOrder[] } | RestaurantOrder[]>(
            `/restaurant/dine/${encodeURIComponent(qrToken)}/orders`,
          );
          const rows = Array.isArray(r.data)
            ? r.data
            : (r.data?.orders ?? []);
          if (rows.length || placedIds.length === 0) {
            setTickets(rows.map(parseTicket));
            return;
          }
        } catch {
          // fall through to legacy fan-out
        }
      }

      // 2. Legacy fan-out — needs auth, only works with guest provisioning.
      if (placedIds.length === 0) {
        setTickets([]);
        return;
      }
      const rows = await Promise.all(
        placedIds.map((id) => restaurantOrderService.get(id).catch(() => null)),
      );
      const parsed = rows
        .filter((r): r is RestaurantOrder => !!r)
        .map(parseTicket);
      setTickets(parsed);
    } finally {
      setLoading(false);
    }
  }, [qrToken, placedIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void refresh();
    if (placedIds.length === 0 && !qrToken) return;
    const t = window.setInterval(() => void refresh(), 15_000);
    return () => window.clearInterval(t);
  }, [refresh, placedIds.length, qrToken]);

  return { tickets, loading, refresh };
}

function parseTicket(r: RestaurantOrder): DineOrderTicket {
  let parsed: { items?: Array<{ name: string; quantity: number; note?: string }>; placedAt?: string } = {};
  try {
    if (r.description && r.description.startsWith('{')) parsed = JSON.parse(r.description);
  } catch { /* leave empty */ }
  const valid = (ORDER_STATUSES as readonly string[]).includes(r.status ?? '')
    ? (r.status as RestaurantOrderStatus)
    : 'Placed';
  return {
    id: String(r.id ?? ''),
    code: r.code ?? `#${r.id ?? ''}`,
    status: valid,
    placedAt: parsed.placedAt ?? r.indate,
    items: parsed.items,
  };
}
